import { ReactNode } from 'react';
import { IControlProps, IPartialFormGroup } from '../types';
import type { IOption } from '../../types/IOption';
import FormControl from '../FormControl';
import { useHookFormController } from '../../hooks/useHookFormController';
import Select, { ISelectProps } from '../../atoms/Select';
import YesNoSegmented from './YesNoSegmented';

export type IYesNoControlProps = Omit<ISelectProps, 'onChange' | 'values'> &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        trueText?: string;
        falseText?: string;
        /** Старый вид — дропдаун Select (из XML-конфига поля). */
        legacy?: boolean;
        /** null приходит при сбросе значения — чтобы отработали зависимые <changes>. */
        onValueChange?: (value: number | null) => void;
    };

const YesNoControl = ({
    name,
    rules,
    control,
    shouldUnregister,
    formGroupProps,
    label,
    tooltip,
    required,
    legacy,
    disabled,
    testid,
    onValueChange,
    trueText,
    falseText,
    ...props
}: IYesNoControlProps) => {
    const Controller = useHookFormController();

    // Число из формы: 1 / 0 / null (undefined трактуем как null).
    const toNum = (value: unknown): number | null =>
        value === 1 || value?.toString() === '1' ? 1 : value === 0 || value?.toString() === '0' ? 0 : null;

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            render={({ field: { onChange, ref, value, ...fieldProps } }) => {
                const numValue = toNum(value);
                const commit = (val: number | null) => {
                    if (numValue !== val) {
                        onChange(val);
                        onValueChange?.(val);
                    }
                };

                return (
                    <FormControl
                        readonlyAppearance={disabled ? (legacy ? 'field' : 'hint') : undefined}
                        name={name}
                        label={label}
                        tooltip={tooltip}
                        required={required}
                        control={control}
                        {...formGroupProps}
                    >
                        {legacy ? (
                            <Select
                                {...fieldProps}
                                {...props}
                                ref={ref}
                                disabled={disabled}
                                testid={testid}
                                options={[
                                    { value: '1', label: trueText ?? 'Да' },
                                    { value: '0', label: falseText ?? 'Нет' },
                                ]}
                                onlySelect={true}
                                readOnly={true}
                                values={[
                                    {
                                        value: numValue?.toString() ?? '',
                                        label:
                                            numValue === 1
                                                ? (trueText ?? 'Да')
                                                : numValue === 0
                                                  ? (falseText ?? 'Нет')
                                                  : '',
                                    },
                                ]}
                                onChange={(option: IOption[]) => {
                                    if (option.length > 0) {
                                        commit(option[0].value == '1' ? 1 : 0);
                                    }
                                }}
                            />
                        ) : (
                            <YesNoSegmented
                                // фокус формы — на выбранный сегмент или первый (у них tabIndex 0)
                                ref={(group: HTMLDivElement | null) =>
                                    ref(
                                        group && {
                                            focus: () => group.querySelector<HTMLElement>('[tabindex="0"]')?.focus(),
                                        },
                                    )
                                }
                                readonlyHint={false}
                                value={numValue}
                                onChange={commit}
                                onClear={required ? undefined : () => commit(null)}
                                trueText={trueText}
                                falseText={falseText}
                                disabled={disabled}
                                // с подписью имя группы — она (FormGroup); без неё — имя поля
                                ariaLabel={label ? undefined : name}
                                testid={testid}
                            />
                        )}
                    </FormControl>
                );
            }}
        />
    );
};

export default YesNoControl;
